import { reactive } from 'vue';
import makeStoreDebugAccessible from '@/core/store/store-debug';
import {
    AgentCustomer,
    B2BClientService,
    Response, StopImpersonationResponse,
} from '@/b2b/shared/api/server-apis';
import utilities from '@/shared/utilities.service';
import authStore from '@/shared/auth/auth.store';
import { hardRedirect } from '@/core/navigation/redirect.service';
import siteContextStore from '@/shared/content/site-context.store';

class AgentsStore {
    private state: IAgentsState = reactive({
        agents: [],
        impersonation: null,
    });

    constructor() {
        makeStoreDebugAccessible('agentsStore', this.state);
    }

    get agents(): AgentCustomer[] {
        return this.state.agents;
    }

    get isImpersonating(): boolean {
        return authStore.userData?.agentImpersonatorUserName ?? false;
    }

    get impersonatedUserAccountName(): string {
        return authStore.userData.accountName;
    }

    async impersonate(agent: AgentCustomer): Promise<Response | undefined> {
        const response = await B2BClientService.impersonateUserAsAgent(agent.impersonateUserId ?? null).then(utilities.unwrapModel);
        if (response?.accessToken) {
            authStore.resetAuthentication(response.accessToken);
            hardRedirect(siteContextStore.settings.frontPageUrl);
        }
        return response;
    }

    async unImpersonate(): Promise<StopImpersonationResponse | undefined> {
        const response = await B2BClientService.stopImpersonateUserAsAgent().then(utilities.unwrapModel);
        if (response?.success) {
            authStore.resetAuthentication(response.token, response.refreshToken);
            response.redirectUrl && hardRedirect(response.redirectUrl);
        }
        return response;
    }

    async updateAgents(): Promise<AgentCustomer[]> {
        const agents = await B2BClientService.getCustomersForAgent().then(utilities.unwrapModel);
        return this.state.agents = agents || [];
    }
}

export interface IAgentsState {
    agents: AgentCustomer[];
    impersonation: AgentCustomer | null;
}

export default new AgentsStore();
